import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  ButtonBase,
  Avatar,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import DateRangeFilter, { DateRangeChip } from '../shared/DateRangeFilter';
import { TOKENS, avatarColors } from '../theme';

// Spec §2 — root screen. List of distinct employees (by name), filterable
// by search + date range. The filter icon (positioned before the search
// input) opens a date-range dialog modelled on MasterReadingTable.
export default function EmployeesScreen({
  sessions,
  onSelectEmployee,
  fromDate,
  toDate,
  onDateChange,
  onDateReset,
  colorOf,
}) {
  const [query, setQuery] = useState('');
  const openFilterRef = useRef(null);

  const employees = useMemo(() => {
    const map = new Map();
    (sessions || []).forEach((s) => {
      if (!s.name) return;
      const existing = map.get(s.name) || {
        name: s.name,
        userId: s.userId,
        sessionCount: 0,
      };
      existing.sessionCount += 1;
      existing.userId = existing.userId || s.userId;
      map.set(s.name, existing);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [sessions]);

  const filtered = useMemo(() => {
    if (!query.trim()) return employees;
    const q = query.toLowerCase();
    return employees.filter((e) => e.name.toLowerCase().includes(q));
  }, [employees, query]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: 26, fontWeight: 700, color: TOKENS.slate900 }}
        >
          Employees
        </Typography>
        <Typography sx={{ fontSize: 15, color: TOKENS.slate500, mt: 0.25 }}>
          Pick an employee to see their sessions
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          alignItems: 'center',
          columnGap: 1,
          rowGap: 1,
          mb: 2,
        }}
      >
        {onDateChange && (
          <React.Fragment>
            {/* chip sits above Search, in Search's column */}
            <Box
              sx={{
                gridColumn: 2,
                display: 'flex',
                '&:empty': { display: 'none' },
              }}
            >
              <DateRangeChip
                fromDate={fromDate}
                toDate={toDate}
                onClick={() => openFilterRef.current && openFilterRef.current()}
                onReset={onDateReset}
              />
            </Box>
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onDateChange={onDateChange}
              openRef={openFilterRef}
            />
          </React.Fragment>
        )}
        <TextField
          sx={{ gridColumn: onDateChange ? 2 : '1 / -1' }}
          fullWidth
          size="small"
          placeholder="Search employees..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: TOKENS.slate400, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {filtered.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: TOKENS.slate400,
              fontSize: 13,
            }}
          >
            No employees found.
          </Box>
        )}
        {filtered.map((e, i) => (
          <ButtonBase
            key={e.name + i}
            onClick={() => onSelectEmployee(e)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              borderBottom:
                i < filtered.length - 1
                  ? `1px solid ${TOKENS.slate100}`
                  : 'none',
              textAlign: 'left',
              justifyContent: 'flex-start',
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                ...avatarColors(e.userId, e.name, colorOf),
                fontWeight: 700,
                fontSize: 18,
                mr: 1.5,
              }}
            >
              {e.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 16, fontWeight: 700, color: TOKENS.slate900 }}
                noWrap
              >
                {e.name}
              </Typography>
              <Typography sx={{ fontSize: 13, color: TOKENS.slate500 }}>
                {e.sessionCount} {e.sessionCount === 1 ? 'session' : 'sessions'}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: TOKENS.slate400 }} />
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
